import { PlayerRef, Player as RemotionPlayer } from "@remotion/player";
import { MyComposition } from "@repo/video-engine-i2v/Composition";
import { useEditorStore } from "../../../lib/stores/editor-store/editor-store";
import { calculateDurationInFrames } from "@repo/video-engine-i2v/utils/timing";
import { I2VCompositionProps } from "@repo/api/editor";
import { PlayerWrapper } from "./PlayerWrapper";
import { useEffect, useMemo, useRef } from "react";
import { getResolution } from "@repo/api/utils/composition";

export const VIDEO_PREVIEW_CONTAINER_ID = "video-preview-container";

export default function VideoPreview() {
  const composition = useEditorStore((state) => state.video?.compositionJson);
  const sourceImages = useEditorStore((state) => state.sourceImages);
  const animatedImages = useEditorStore((state) => state.animatedImages);
  const voiceOvers = useEditorStore((state) => state.voiceOvers);
  const allBackgroundMusics = useEditorStore((state) => state.backgroundMusics);
  const backgroundMusics = useMemo(
    () => Object.values(allBackgroundMusics),
    [allBackgroundMusics],
  );
  const assetMedias = useEditorStore((state) => state.assetMedias);
  const videoPreviewState = useEditorStore((state) => state.videoPreviewState);
  const fonts = useEditorStore((state) => state.compositionFonts);

  const fontsMap = useMemo(
    () => Object.fromEntries(fonts.map((font) => [font.externalId, font])),
    [fonts],
  );

  if (!composition || composition?.mainSceneTimeline.length === 0) {
    return null;
  }

  const durationInFrames = calculateDurationInFrames(
    composition,
    animatedImages,
  );
  const { width, height } = getResolution(composition.aspectRatio);

  const inputProps: I2VCompositionProps = {
    composition,
    sourceImages,
    animatedImages,
    voiceOvers,
    backgroundMusics,
    assetMedias,
    durationInFrames,
    fps: composition.fps,
    isStudio: true,
    isPlaying: videoPreviewState.isPlaying,
    isFullscreen: videoPreviewState.isFullscreen,
    fonts: fontsMap,
  };

  if (durationInFrames < 1) {
    return null;
  }

  return (
    <div
      id={VIDEO_PREVIEW_CONTAINER_ID}
      className="flex h-full flex-1 flex-col overflow-hidden px-3 py-0 2xl:px-10"
    >
      <div className="h-14"></div>
      <PlayerWrapper
        targetWidth={width}
        targetHeight={height}
        timelineItems={composition.mainSceneTimeline}
        animatedImages={animatedImages}
        sourceImages={sourceImages}
        durationInFrames={durationInFrames}
        fps={composition.fps}
      >
        <Player
          durationInFrames={durationInFrames}
          width={width}
          height={height}
          inputProps={inputProps}
          fps={composition.fps}
        />
      </PlayerWrapper>
      <div className="h-24"></div>
    </div>
  );
}

type PlayerProps = {
  durationInFrames: number;
  width: number;
  height: number;
  inputProps: I2VCompositionProps;
  fps: number;
};

// Do not add early exit to this component. Otherwise, the playerRef will not work.
function Player({
  durationInFrames,
  width,
  height,
  inputProps,
  fps,
}: PlayerProps) {
  const getCurrentFrame = useEditorStore(
    (state) => state.service.getCurrentFrame,
  );
  const playerRef = useRef<PlayerRef | null>(null);
  const setCurrentFrame = useEditorStore(
    (state) => state.service.setCurrentFrame,
  );
  const updateVideoPreviewState = useEditorStore(
    (state) => state.service.updateVideoPreviewState,
  );

  const setPlayerRef = useEditorStore((state) => state.service.setPlayerRef);

  const isFullscreen = useEditorStore(
    (state) => state.videoPreviewState.isFullscreen,
  );

  useEffect(() => {
    setPlayerRef(playerRef);

    return () => {
      setPlayerRef(null);
    };
  }, [setPlayerRef]);

  useEffect(() => {
    const currentRef = playerRef.current;

    const handleTimeUpdate = (e: { detail: { frame: number } }) => {
      setCurrentFrame(e.detail.frame);
    };

    const handlePlay = () =>
      updateVideoPreviewState((state) => {
        state.isPlaying = true;
      });
    const handlePause = () =>
      updateVideoPreviewState((state) => {
        state.isPlaying = false;
      });

    const handleFullscreenChange = (e: {
      detail: { isFullscreen: boolean };
    }) => {
      updateVideoPreviewState((state) => {
        state.isFullscreen = e.detail.isFullscreen;
      });
    };

    const handleMuteChange = (e: { detail: { isMuted: boolean } }) => {
      updateVideoPreviewState((state) => {
        state.isMuted = e.detail.isMuted;
      });
    };

    if (currentRef) {
      updateVideoPreviewState((state) => {
        state.isPlaying = currentRef.isPlaying();
        state.isFullscreen = currentRef.isFullscreen();
        state.isMuted = currentRef.isMuted();
      });

      currentRef.addEventListener("timeupdate", handleTimeUpdate);
      currentRef.addEventListener("play", handlePlay);
      currentRef.addEventListener("pause", handlePause);
      currentRef.addEventListener("fullscreenchange", handleFullscreenChange);
      currentRef.addEventListener("mutechange", handleMuteChange);
    }

    return () => {
      if (currentRef) {
        currentRef.removeEventListener("timeupdate", handleTimeUpdate);
        currentRef.removeEventListener("play", handlePlay);
        currentRef.removeEventListener("pause", handlePause);
        currentRef.removeEventListener(
          "fullscreenchange",
          handleFullscreenChange,
        );
        currentRef.removeEventListener("mutechange", handleMuteChange);
      }
    };
  }, [setCurrentFrame, updateVideoPreviewState]);

  return (
    <RemotionPlayer
      ref={playerRef}
      initialFrame={Math.min(getCurrentFrame(), durationInFrames - 1)}
      component={MyComposition}
      durationInFrames={durationInFrames}
      compositionWidth={width}
      compositionHeight={height}
      inputProps={inputProps}
      controls={isFullscreen}
      fps={fps}
      acknowledgeRemotionLicense
      overflowVisible
      autoPlay={false}
      initiallyMuted={false}
      initialVolume={1}
      moveToBeginningWhenEnded={false}
      renderMuteButton={() => null}
      style={{
        height: "100%",
        width: "100%",
        zIndex: 10,
      }}
    />
  );
}
